import { useEffect } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import ServiceHeader from "@/components/ServiceHeader";
import Footer from "@/components/Footer";
import StickyCallButton from "@/components/StickyCallButton";
import { upsertCanonical, upsertLdJson, upsertMeta } from "@/lib/head";
import { suburbs } from "@/content/suburbs";

const TITLE = "Moving Service Locations | OZ WASTE CO.";
const DESCRIPTION =
  "Explore OZ WASTE CO. moving service locations, including Melbourne and upcoming expansion across major Australian cities.";
const URL = "https://ozwasteco.com.au/locations";

const COMING_SOON_COPY =
  "We're expanding across Australia. Contact our team to check current availability.";

const locations = [
  {
    city: "Melbourne",
    href: "/melbourne",
    status: "Now Servicing" as const,
    description:
      "Professional residential and commercial moving services across Melbourne.",
  },
  { city: "Sydney", href: "/sydney", status: "Coming Soon" as const, description: COMING_SOON_COPY },
  { city: "Brisbane", href: "/brisbane", status: "Coming Soon" as const, description: COMING_SOON_COPY },
  { city: "Perth", href: "/perth", status: "Coming Soon" as const, description: COMING_SOON_COPY },
  { city: "Adelaide", href: "/adelaide", status: "Coming Soon" as const, description: COMING_SOON_COPY },
  { city: "Gold Coast", href: "/gold-coast", status: "Coming Soon" as const, description: COMING_SOON_COPY },
];

const Locations = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    const desc = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content") ?? "";
    desc?.setAttribute("content", DESCRIPTION);
    const cleanups = [
      upsertMeta("property", "og:title", TITLE),
      upsertMeta("property", "og:description", DESCRIPTION),
      upsertMeta("property", "og:url", URL),
      upsertMeta("name", "twitter:title", TITLE),
      upsertMeta("name", "twitter:description", DESCRIPTION),
      upsertCanonical(URL),
      upsertLdJson("ld-locations-page", {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: TITLE,
        description: DESCRIPTION,
        url: URL,
        mainEntity: {
          "@type": "ItemList",
          itemListElement: locations.map((loc, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: `${loc.city}: ${loc.status}`,
            url: `https://ozwasteco.com.au${loc.href}`,
          })),
        },
      }),
      upsertLdJson("ld-locations-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://ozwasteco.com.au/" },
          { "@type": "ListItem", position: 2, name: "Locations", item: URL },
        ],
      }),
    ];
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      desc?.setAttribute("content", prevDesc);
      cleanups.forEach((fn) => fn());
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <ServiceHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-[#FDC00E] relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(255,255,255,0.55),transparent_55%)]" aria-hidden="true" />
          <div className="container relative py-20 md:py-28">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-black text-[#1a1a1a] tracking-tight leading-tight">
                OZ WASTE CO. Locations
              </h1>
              <p className="mt-5 text-lg text-[#1a1a1a]/80 leading-relaxed">
                Explore our current and upcoming moving service locations across Australia.
              </p>
              <div className="mt-9">
                <a
                  href="/#quote"
                  className="inline-block bg-[#1a1a1a] text-white px-8 py-4 text-lg font-bold rounded-xl hover:bg-orange transition-colors"
                >
                  Get a Free Quote
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Location grid */}
        <section className="py-16 md:py-24">
          <div className="container">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1a1a1a] tracking-tight">
              Where We Move
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl">
              {locations.map((loc) => {
                const active = loc.status === "Now Servicing";
                return (
                  <div
                    key={loc.city}
                    className="flex flex-col bg-white/70 backdrop-blur-xl border border-[#1a1a1a]/8 shadow-[0_8px_40px_rgba(26,26,26,0.06)] rounded-[2rem] p-7 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_18px_50px_rgba(26,26,26,0.1)]"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-[#1a1a1a]" strokeWidth={1.8} />
                        <h3 className="text-xl font-semibold text-[#1a1a1a]">{loc.city}</h3>
                      </div>
                      <span
                        className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${
                          active
                            ? "bg-[#FDC00E] text-[#1a1a1a]"
                            : "bg-[#1a1a1a]/8 text-[#1a1a1a]/70"
                        }`}
                      >
                        {loc.status}
                      </span>
                    </div>
                    <p className="mt-4 text-[#1a1a1a]/70 text-sm leading-relaxed flex-1">
                      {loc.description}
                    </p>
                    <a
                      href={active ? loc.href : "/contact"}
                      aria-label={
                        active ? `View Melbourne moving services` : `Contact us about moving in ${loc.city}`
                      }
                      className="group mt-6 inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-white px-6 py-3 font-bold rounded-xl hover:bg-orange transition-colors"
                    >
                      {active ? `View ${loc.city}` : "Contact"}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Melbourne service area */}
        <section className="bg-[#fafafa] py-16 md:py-24">
          <div className="container max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1a1a1a] tracking-tight">
              Melbourne Service Area
            </h2>
            <p className="mt-5 text-[#1a1a1a]/75 text-lg leading-relaxed">
              OZ WASTE CO. currently services Melbourne and the surrounding metropolitan areas:
              inner city apartments, suburban homes and commercial premises across the north, south,
              east and west. If you are unsure whether your suburb is covered, call us and we will
              confirm straight away.
            </p>
            <a
              href="/melbourne"
              className="mt-7 inline-flex items-center gap-2 font-bold text-[#1a1a1a] underline decoration-[#FDC00E] decoration-2 underline-offset-4 hover:opacity-80 transition-opacity"
            >
              See Melbourne rubbish removal services <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>

        {/* Suburb pages */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1a1a1a] tracking-tight">
              Melbourne Suburbs We Service
            </h2>
            <p className="mt-5 text-[#1a1a1a]/75 text-lg leading-relaxed max-w-3xl">
              Dedicated rubbish removal services across Melbourne's north, residential and
              commercial.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {suburbs.map((s) => (
                <a
                  key={s.slug}
                  href={`/${s.slug}`}
                  className="group flex items-center gap-3 bg-white/70 backdrop-blur-xl border border-[#1a1a1a]/8 shadow-[0_8px_40px_rgba(26,26,26,0.06)] rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(26,26,26,0.1)]"
                >
                  <MapPin className="w-5 h-5 text-[#1a1a1a] shrink-0" strokeWidth={1.8} />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#1a1a1a]">{s.name}</h3>
                    <p className="text-sm text-[#1a1a1a]/60">
                      {s.kind === "commercial" ? "Commercial & industrial" : "Residential"}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#1a1a1a] transition-transform group-hover:translate-x-1" />
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Future expansion */}
        <section className="py-16 md:py-24">
          <div className="container max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1a1a1a] tracking-tight">
              Future Expansion
            </h2>
            <p className="mt-5 text-[#1a1a1a]/75 text-lg leading-relaxed">
              We're continuing to expand into more Australian cities. Check back for new service
              areas or contact our team to discuss availability.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="border-2 border-[#1a1a1a] text-[#1a1a1a] px-8 py-4 text-lg font-bold rounded-xl hover:bg-orange/10 hover:border-orange hover:text-orange-deep transition-colors"
              >
                Contact
              </a>
              <a
                href="/#quote"
                className="bg-[#1a1a1a] text-white px-8 py-4 text-lg font-bold rounded-xl hover:bg-orange transition-colors"
              >
                Get a Free Quote
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default Locations;